import { formatBRL } from "@yatta/domain";
import { CheckCircle2, Copy, FileText, LockKeyhole, QrCode } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button, Card, Field } from "../components/ui";
import { useTenantRuntime } from "../config/TenantRuntime";
import { cruzeiroLifePaymentReceivables } from "../data/cruzeiro-life-payment-mock-data";
import { env } from "../lib/env";
import { supabase } from "../lib/supabase";

interface PublicReceivable {
  installmentId: string;
  agreementNumber: string;
  agreementType: string;
  assetName: string;
  sequence: number;
  dueDate: string;
  originalAmountCents: string;
  paidAmountCents: string;
  status: string;
  daysLate: number;
  lateFeeCents: string;
  interestCents: string;
  correctionCents: string;
  updatedAmountCents: string;
}

type Step = "IDENTIFIER" | "OTP" | "LIST" | "PROCESSING" | "ISSUED";

export function isDemoIdentifierValid(value: string) {
  return [11, 14].includes(value.replace(/\D/g, "").length);
}

export function receivablesState(receivables: readonly PublicReceivable[]) {
  return receivables.length === 0 ? "empty" : "ready";
}

function asError(value: unknown): Error {
  return value instanceof Error
    ? value
    : new Error(typeof value === "string" ? value : "Portal de pagamento indisponível.");
}

async function invokePortal<T>(body: Record<string, unknown>): Promise<T> {
  const response = await supabase.functions.invoke("public-payment-portal", {
    body,
  });
  const error: unknown = response.error;
  if (error) throw asError(error);
  const data: unknown = response.data;
  return data as T;
}

export function PublicSecondCopyPage() {
  const { tenant } = useTenantRuntime();
  const [step, setStep] = useState<Step>("IDENTIFIER");
  const [identifier, setIdentifier] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [otp, setOtp] = useState("");
  const [token, setToken] = useState("");
  const [destination, setDestination] = useState("");
  const [receivables, setReceivables] = useState<PublicReceivable[]>([]);
  const [selected, setSelected] = useState<PublicReceivable | null>(null);
  const [issued, setIssued] = useState<{
    method: "PIX" | "BOLETO";
    amountCents: string;
    paymentUrl?: string;
    pixCopyPaste?: string;
    boletoBarcode?: string;
  } | null>(null);
  const [processing, setProcessing] = useState<{
    requestId: string;
    amountCents: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const cleanIdentifier = useMemo(() => identifier.replace(/\D/g, ""), [identifier]);

  const applyIssuedResult = useCallback(
    (result: {
      method?: "PIX" | "BOLETO";
      amountCents: string;
      paymentUrl?: string;
      pixCopyPaste?: string;
      boletoBarcode?: string;
    }) => {
      if (!result.method) {
        throw new Error("Resposta de pagamento incompleta.");
      }
      setIssued({
        method: result.method,
        amountCents: result.amountCents,
        ...(result.paymentUrl ? { paymentUrl: result.paymentUrl } : {}),
        ...(result.pixCopyPaste ? { pixCopyPaste: result.pixCopyPaste } : {}),
        ...(result.boletoBarcode ? { boletoBarcode: result.boletoBarcode } : {}),
      });
      setProcessing(null);
      setStep("ISSUED");
    },
    [],
  );

  const refreshProcessingStatus = useCallback(
    async (silent = false): Promise<boolean> => {
      if (!processing?.requestId || !token || env.useMocks) return false;
      if (!silent) setLoading(true);

      try {
        const result = await invokePortal<{
          requestId: string;
          status: string;
          method?: "PIX" | "BOLETO";
          amountCents: string;
          paymentUrl?: string;
          pixCopyPaste?: string;
          boletoBarcode?: string;
          message?: string;
        }>({
          action: "status",
          token,
          requestId: processing.requestId,
        });

        if (result.status === "ISSUED") {
          applyIssuedResult(result);
          return true;
        }

        if (
          result.status === "FAILED" ||
          result.status === "EXPIRED" ||
          result.status === "CANCELLED"
        ) {
          setProcessing(null);
          setStep("LIST");
          toast.error(result.message ?? "Não foi possível concluir a emissão. Tente novamente.");
          return true;
        }

        setProcessing((current) =>
          current
            ? {
                ...current,
                amountCents: result.amountCents ?? current.amountCents,
              }
            : current,
        );
        return false;
      } catch (error) {
        if (!silent) {
          toast.error(
            error instanceof Error ? error.message : "Não foi possível consultar a emissão.",
          );
        }
        return false;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [applyIssuedResult, processing?.requestId, token],
  );

  useEffect(() => {
    if (step !== "PROCESSING" || !processing?.requestId || !token || env.useMocks) {
      return;
    }

    let active = true;
    let attempts = 0;
    const maximumAttempts = 30;

    const poll = async () => {
      if (!active || attempts >= maximumAttempts) return;
      attempts += 1;
      const finished = await refreshProcessingStatus(true);
      if (finished || !active) return;
    };

    void poll();
    const timer = window.setInterval(() => {
      void poll();
      if (attempts >= maximumAttempts) {
        window.clearInterval(timer);
      }
    }, 2500);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [processing?.requestId, refreshProcessingStatus, step, token]);

  const requestCode = async () => {
    setErrorMessage(null);
    if (!isDemoIdentifierValid(cleanIdentifier)) {
      const message = "Informe um CPF ou CNPJ válido.";
      setErrorMessage(message);
      toast.error(message);
      return;
    }

    setLoading(true);
    try {
      if (env.useMocks) {
        setChallengeId("mock-challenge");
        setDestination("WhatsApp final 0182");
        setStep("OTP");
        toast.success("Código demo enviado. Use 123456.");
        return;
      }

      const result = await invokePortal<{
        accepted: boolean;
        challengeId?: string;
        destinationMasked?: string;
      }>({
        action: "request",
        tenantSlug: tenant.slug,
        identifier: cleanIdentifier,
      });

      if (!result.challengeId) {
        toast.success("Se os dados estiverem cadastrados, enviaremos um código de acesso.");
        return;
      }
      setChallengeId(result.challengeId);
      setDestination(result.destinationMasked ?? "contato cadastrado");
      setStep("OTP");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Falha ao solicitar código.";
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    setErrorMessage(null);
    if (otp.replace(/\D/g, "").length !== 6) {
      const message = "Informe o código de 6 dígitos.";
      setErrorMessage(message);
      toast.error(message);
      return;
    }

    setLoading(true);
    try {
      if (env.useMocks) {
        if (otp !== "123456") throw new Error("Código demo inválido.");
        setToken("mock-session");
        setReceivables(tenant.slug === "luto-cruzeiro" ? cruzeiroLifePaymentReceivables : []);
        setStep("LIST");
        return;
      }

      const result = await invokePortal<{
        token: string;
        receivables: PublicReceivable[];
      }>({
        action: "verify",
        challengeId,
        otp,
      });
      setToken(result.token);
      setReceivables(result.receivables);
      setStep("LIST");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Código inválido.";
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const issueCopy = async (method: "PIX" | "BOLETO") => {
    if (!selected) return;
    setErrorMessage(null);
    setLoading(true);
    try {
      if (env.useMocks) {
        const mockIssue = {
          method,
          amountCents: selected.updatedAmountCents,
          paymentUrl: "https://pagamento.demo.yatta.app.br/segunda-via",
          ...(method === "PIX"
            ? { pixCopyPaste: `000201YATTADEMO${selected.installmentId}` }
            : {
                boletoBarcode: "34191.79001 01043.510047 91020.150008 5 99990000204600",
              }),
        };
        setIssued(mockIssue);
        setStep("ISSUED");
        return;
      }

      const result = await invokePortal<{
        requestId?: string;
        status?: string;
        method?: "PIX" | "BOLETO";
        amountCents: string;
        paymentUrl?: string;
        pixCopyPaste?: string;
        boletoBarcode?: string;
      }>({
        action: "issue",
        token,
        installmentId: selected.installmentId,
        method,
      });
      if (
        result.status !== "ISSUED" ||
        !result.method ||
        (!result.pixCopyPaste && !result.boletoBarcode && !result.paymentUrl)
      ) {
        if (!result.requestId) {
          throw new Error("Protocolo da emissão não foi informado.");
        }
        setProcessing({
          requestId: result.requestId,
          amountCents: result.amountCents,
        });
        setStep("PROCESSING");
        return;
      }
      applyIssuedResult(result);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Falha ao emitir segunda via.";
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="public-page">
      <header className="onboarding-header">
        <div className="brand-mark">
          <FileText />
        </div>
        <div>
          <strong>{tenant.branding.shortName}</strong>
          <span>Segunda via rápida e segura</span>
        </div>
      </header>

      <div className="public-payment-layout">
        <Card className="public-payment-card">
          {errorMessage && (
            <div className="inline-notice" role="alert">
              <LockKeyhole size={18} />
              <div>
                <strong>Não foi possível continuar</strong>
                <span>{errorMessage}</span>
              </div>
            </div>
          )}
          {step === "IDENTIFIER" && (
            <div className="form-section compact-section">
              <div>
                <p className="eyebrow">PAGAMENTOS</p>
                <h1>Consulte suas parcelas</h1>
                <p className="muted-copy">
                  Informe o CPF ou CNPJ do responsável. Para sua segurança, enviaremos um código ao
                  contato cadastrado antes de mostrar os recebíveis.
                </p>
              </div>
              <Field label="CPF ou CNPJ">
                <input
                  inputMode="numeric"
                  autoComplete="off"
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  placeholder="Somente números"
                />
              </Field>
              <Button disabled={loading} onClick={() => void requestCode()}>
                <LockKeyhole size={17} />
                {loading ? "Enviando..." : "Receber código de acesso"}
              </Button>
            </div>
          )}

          {step === "OTP" && (
            <div className="form-section compact-section">
              <div>
                <p className="eyebrow">VERIFICAÇÃO</p>
                <h1>Digite o código</h1>
                <p className="muted-copy">
                  Enviamos um código de 6 dígitos para {destination || "o contato cadastrado"}.
                </p>
              </div>
              <Field label="Código de segurança">
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                />
              </Field>
              {env.useMocks && (
                <div className="demo-warning">Ambiente demo: use o código 123456.</div>
              )}
              <div className="wizard-actions inline-actions">
                <Button variant="secondary" onClick={() => setStep("IDENTIFIER")}>
                  Voltar
                </Button>
                <Button disabled={loading} onClick={() => void verifyCode()}>
                  {loading ? "Verificando..." : "Acessar parcelas"}
                </Button>
              </div>
            </div>
          )}

          {step === "LIST" && (
            <div className="form-section compact-section">
              <div>
                <p className="eyebrow">RECEBÍVEIS EM ABERTO</p>
                <h1>Escolha uma parcela</h1>
                <p className="muted-copy">
                  Valores vencidos já aparecem atualizados conforme as regras do contrato.
                </p>
              </div>

              <div className="public-receivable-list">
                {receivables.map((item) => (
                  <button
                    type="button"
                    aria-pressed={selected?.installmentId === item.installmentId}
                    aria-label={`Selecionar parcela ${item.sequence}, ${formatBRL(BigInt(item.updatedAmountCents))}`}
                    className={
                      selected?.installmentId === item.installmentId
                        ? "public-receivable selected"
                        : "public-receivable"
                    }
                    key={item.installmentId}
                    onClick={() => setSelected(item)}
                  >
                    <div>
                      <strong>{item.assetName}</strong>
                      <span>
                        {item.agreementNumber} · parcela {item.sequence}
                      </span>
                      <small>
                        Vencimento{" "}
                        {new Date(`${item.dueDate}T12:00:00`).toLocaleDateString("pt-BR")}
                        {item.daysLate > 0 ? ` · ${item.daysLate} dias em atraso` : ""}
                      </small>
                    </div>
                    <div className="receivable-amount">
                      <span>Valor atualizado</span>
                      <strong>{formatBRL(BigInt(item.updatedAmountCents))}</strong>
                    </div>
                  </button>
                ))}
              </div>

              {receivablesState(receivables) === "empty" && (
                <div className="empty-state" role="status">
                  <strong>Nenhum pagamento em aberto</strong>
                  <p>Não encontramos parcelas disponíveis para segunda via.</p>
                </div>
              )}

              {selected && (
                <div className="payment-method-actions">
                  <Button disabled={loading} onClick={() => void issueCopy("PIX")}>
                    <QrCode size={17} /> Emitir Pix
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={loading}
                    onClick={() => void issueCopy("BOLETO")}
                  >
                    <FileText size={17} /> Emitir boleto
                  </Button>
                </div>
              )}
            </div>
          )}

          {step === "PROCESSING" && processing && (
            <div className="form-section compact-section success-payment">
              <FileText size={52} />
              <div>
                <p className="eyebrow">SOLICITAÇÃO RECEBIDA</p>
                <h1>Segunda via em processamento</h1>
                <p className="muted-copy">
                  A Paglog está registrando a cobrança atualizada. Nenhuma nova parcela será criada
                  se você repetir a solicitação.
                </p>
                <strong className="public-payment-total">
                  {formatBRL(BigInt(processing.amountCents))}
                </strong>
                <small>Protocolo: {processing.requestId}</small>
              </div>
              <div className="payment-method-actions">
                <Button disabled={loading} onClick={() => void refreshProcessingStatus(false)}>
                  {loading ? "Consultando..." : "Atualizar agora"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setProcessing(null);
                    setStep("LIST");
                  }}
                >
                  Voltar às parcelas
                </Button>
              </div>
              <small>A consulta é atualizada automaticamente por até 75 segundos.</small>
            </div>
          )}

          {step === "ISSUED" && issued && (
            <div className="form-section compact-section success-payment">
              <CheckCircle2 size={52} />
              <div>
                <p className="eyebrow">SEGUNDA VIA EMITIDA</p>
                <h1>{issued.method === "PIX" ? "Pix disponível" : "Boleto disponível"}</h1>
                <p className="muted-copy">Valor atualizado</p>
                <strong className="public-payment-total">
                  {formatBRL(BigInt(issued.amountCents))}
                </strong>
              </div>

              {issued.pixCopyPaste && (
                <div className="copy-box">
                  <code>{issued.pixCopyPaste}</code>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      void navigator.clipboard.writeText(issued.pixCopyPaste ?? "");
                      toast.success("Código Pix copiado.");
                    }}
                  >
                    <Copy size={16} /> Copiar
                  </Button>
                </div>
              )}

              {issued.boletoBarcode && (
                <div className="copy-box">
                  <code>{issued.boletoBarcode}</code>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      void navigator.clipboard.writeText(issued.boletoBarcode ?? "");
                      toast.success("Linha digitável copiada.");
                    }}
                  >
                    <Copy size={16} /> Copiar
                  </Button>
                </div>
              )}

              <Button
                variant="secondary"
                onClick={() => {
                  setSelected(null);
                  setIssued(null);
                  setStep("LIST");
                }}
              >
                Voltar às parcelas
              </Button>
            </div>
          )}
        </Card>

        <aside className="onboarding-summary">
          <LockKeyhole size={24} />
          <strong>Consulta protegida</strong>
          <p>
            O CPF ou CNPJ é usado apenas para localizar o cadastro. Os dados financeiros só aparecem
            depois da confirmação do código enviado ao contato registrado.
          </p>
          <hr />
          <small>Sessões expiram automaticamente e todas as consultas são auditadas.</small>
        </aside>
      </div>
    </div>
  );
}
